import React, {useCallback, useEffect, useId, useLayoutEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Info, PencilSimple, Target} from '../../icons';
import {alertLevelSeverity, clampPct, formatFullMoney, projectionSeverity, spendPct} from './panelFormatters';
import {ALERT_LEVEL_CRITIC, CHECK_BUDGET_RATIO} from '../../agency/v2/clientsV2Thresholds';

// El cartel flotante del chip vacío: separación contra el chip, margen mínimo contra los bordes de
// la ventana y el ancho de un `-rich`.
const TIP_GAP = 10;
const TIP_EDGE = 8;
const TIP_WIDTH = 340;

/**
 * Posición `fixed` del cartel del chip, medida contra la ventana — el recurso de `LineActionsMenu`.
 *
 * Lo usa la tabla de `/client/:id/lines`: debajo de 1180px `.plv-tw` es un scrollport y recorta
 * cualquier cartel `absolute`. Con una o dos filas no había lado que entrara (QA-VIS-SEGFE-03/-24:
 * con una sola fila se ocultaban 58 de 79 px y aparecía un scroll vertical adentro de la tabla).
 * Abre debajo del chip, alineado a su borde derecho, y hacia arriba sólo si abajo no entra.
 *
 * Mientras no está medido el cartel va con `visibility: hidden` (`.-floating:not(.-placed)`): lo
 * muestra el `:hover`/`:focus-within` de siempre, así que sin eso se vería un frame en el lugar viejo.
 */
function useFloatingTip(enabled) {
    const anchorRef = useRef(null);
    const tipRef = useRef(null);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [pos, setPos] = useState(null);
    const open = enabled && (hovered || focused);

    const measure = useCallback(() => {
        if (!anchorRef.current) return null;
        const rect = anchorRef.current.getBoundingClientRect();
        // ⚠️ `documentElement.clientWidth/Height` y no `window.inner*`: el `fixed` se mide contra el
        // viewport SIN la scrollbar (mismo motivo que en `LineActionsMenu`).
        const viewW = document.documentElement.clientWidth;
        const viewH = document.documentElement.clientHeight;
        const width = Math.min(TIP_WIDTH, viewW - 2 * TIP_EDGE);
        const height = (tipRef.current && tipRef.current.offsetHeight) || 0;
        const left = Math.max(TIP_EDGE, Math.min(rect.right - width, viewW - width - TIP_EDGE));
        const up = rect.bottom + TIP_GAP + height > viewH - TIP_EDGE && rect.top - TIP_GAP - height >= TIP_EDGE;
        // La flecha apunta al centro del chip aunque el cartel se haya corrido para entrar.
        const arrow = Math.max(12, Math.min(rect.left + rect.width / 2 - left - 8, width - 28));
        return {left, width, up, arrow, top: up ? rect.top - TIP_GAP - height : rect.bottom + TIP_GAP};
    }, []);

    useLayoutEffect(() => {
        setPos(open ? measure() : null);
    }, [open, measure]);

    // Anclado a la ventana, no acompaña solo al chip: si algo scrollea mientras está abierto, se
    // vuelve a medir (`capture`, porque el scroll de `.plv-tw` no burbujea).
    useEffect(() => {
        if (!open) return undefined;
        const follow = () => setPos(measure());
        window.addEventListener('scroll', follow, true);
        window.addEventListener('resize', follow);
        return () => {
            window.removeEventListener('scroll', follow, true);
            window.removeEventListener('resize', follow);
        };
    }, [open, measure]);

    if (!enabled) return {anchorProps: {}, tipProps: {}, tipClass: ''};
    return {
        anchorProps: {
            ref: anchorRef,
            onMouseEnter: () => setHovered(true),
            onMouseLeave: () => setHovered(false),
            onFocus: () => setFocused(true),
            onBlur: () => setFocused(false),
        },
        tipProps: {
            ref: tipRef,
            style: pos ? {
                top: pos.top, left: pos.left, width: pos.width, right: 'auto', bottom: 'auto',
                '--pnl-tip-arrow': `${pos.arrow}px`,
            } : undefined,
        },
        tipClass: ` -floating${pos ? ' -placed' : ''}${pos && pos.up ? ' -up' : ''}`,
    };
}

// Los tres objetos visuales de una línea de control que viven en DOS superficies: la fila del Panel
// (`ControlLineRow`) y la vista de todas las líneas (`LinesView`). Extraídos a un módulo propio para
// que el mismo dato se pinte igual en las dos — un chip de objetivo que dice "en ritmo" en el Panel
// y otra cosa en la tabla es el usuario eligiendo a cuál creerle. Todo usa las clases `pnl-*` de
// `ControlLines.scss`, que aplican donde haya un `.pnl-screen`.

/** Una línea eventual con el período ya cerrado: el backend congela su cierre (`LineProjection`). */
export function lineIsFinished(line) {
    return ((line && line.projection) || {}).reason === 'FINISHED';
}

function formatDecimal(value, digits = 1) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    return number.toLocaleString('es-AR', {maximumFractionDigits: digits});
}

/**
 * Chip de objetivo: verde si va en ritmo, oro si no. Sin objetivo cargado NO se inventa un
 * veredicto — se ofrece definirlo, que es la acción que falta. Con objetivo pero sin evaluación
 * (`on_track: null`, la línea no sincronizó) tampoco: el chip lo dice en gris, igual que el punto
 * gris del tile de objetivos del KPI — pintarlo "fuera de ritmo" acusaría algo que nadie verificó.
 *
 * `onDefine` (opcional): abre el modal de objetivos. El chip vacío lo abre ahí mismo; sin la prop
 * navega a la línea completa, como siempre. Con objetivo y `onDefine`, el chip es un botón (con un
 * lápiz) que abre el mismo modal; sin la prop es un rótulo. A una línea TERMINADA no se le pasa
 * (`lineIsFinished`): el período cerró y no hay ritmo contra el cual evaluar un objetivo nuevo.
 *
 * `floatingTip` (opcional): el cartel del porqué se dibuja `fixed` y posicionado por JS. Lo pide la
 * tabla de `/lines`, que recorta; en la card del Panel el cartel se ancla a su fila por CSS.
 */
export function LineGoalChip({goal, lineId, onDefine, floatingTip = false}) {
    const {t} = useTranslation();
    const whyId = useId();
    const floating = useFloatingTip(floatingTip);

    if (!goal || !goal.defined) {
        const label = (
            <>
                <Target size={12} weight="bold"/>
                {t('client_panel.line_goal_define', 'Definir objetivo')}
            </>
        );
        // El chip vacío dice POR QUÉ importa (card mFHzKeNK): sin objetivo la línea es sólo un
        // presupuesto, y la IA no tiene contra qué decir si algo va bien. El cartel cuelga del
        // envoltorio, no del chip, para que su texto no herede el `nowrap` de la pastilla ni caiga
        // adentro del botón (un click sobre el cartel abriría el modal).
        return (
            <span className="info-icon-container pnl-goal-why" {...floating.anchorProps}>
                {onDefine ? (
                    <button type="button" className="pnl-goal -empty" onClick={onDefine}
                            aria-describedby={whyId}>
                        {label}
                    </button>
                ) : (
                    <Link className="pnl-goal -empty" to={`/business-unit/${lineId}/`}
                          aria-describedby={whyId}>
                        {label}
                    </Link>
                )}
                <span className={`warning-tooltip -rich -below-right${floating.tipClass}`} {...floating.tipProps}>
                    <span className="warning-tooltip-body">
                        <span className="warning-tooltip-text" id={whyId}>
                            {t('client_panel.line_goal_why',
                                'Sin objetivo, tu IA sólo puede contar lo que gastó. Con uno, sabe si la línea va bien y tu tablero te avisa si se desvía.')}
                        </span>
                    </span>
                </span>
            </span>
        );
    }

    // Con objetivo, el chip también abre el modal si hay dónde (QA del modal, card o1oqjHps): ahí se
    // corrige la meta o se suma el segundo objetivo. En el Panel es el ÚNICO camino al modal, así que se
    // tiene que ver como control: un lápiz chico a la vista y «Editar objetivos» para el lector de
    // pantalla (no un `title`, que no se ve en táctil ni es del design system; review C-…-05).
    const definedChip = (modifier, text) => (onDefine ? (
        <button type="button" className={`pnl-goal ${modifier} -editable`} onClick={onDefine}>
            {text}
            <PencilSimple size={11} weight="bold" aria-hidden="true"/>
            <span className="visually-hidden">{t('client_panel.line_goal_edit', 'Editar objetivos')}</span>
        </button>
    ) : (
        <span className={`pnl-goal ${modifier}`}>{text}</span>
    ));

    if (goal.on_track !== true && goal.on_track !== false) {
        return definedChip('-stale', t('client_panel.line_goal_stale', '{{metric}} — sin dato reciente', {
            metric: goal.metric_label,
            interpolation: {escapeValue: false},
        }));
    }

    const direction = goal.direction === 'GTE' ? '≥' : '≤';
    return definedChip(goal.on_track ? '-ok' : '-warn',
        t('client_panel.line_goal', '{{metric}} {{current}} · objetivo {{direction}} {{target}}', {
            metric: goal.metric_label,
            current: formatDecimal(goal.current_value, 2),
            direction,
            target: formatDecimal(goal.target_value, 2),
            interpolation: {escapeValue: false},
        }));
}

/**
 * Barra de consumo con la marca del esperado a hoy. Sin presupuesto no hay contra qué medir → no se
 * dibuja nada. Una línea que no sincroniza no puede opinar del ritmo: la barra va al gris neutro y
 * se le saca la marca del esperado — pintarla verde/roja sería afirmar algo sobre datos viejos.
 * El color sale del veredicto de la agencia (`budget_alert_level`), nunca de un umbral propio.
 */
export function LineSpendBar({line}) {
    const budget = line.budget || {};
    if (!(Number(budget.allocated) > 0)) return null;

    const stale = line.data_freshness && line.data_freshness !== 'FRESH';
    const severity = stale ? 'gray' : alertLevelSeverity(line.budget_alert_level);
    const width = clampPct(spendPct(budget.spend, budget.allocated));
    const paceLeft = stale ? null : clampPct(budget.expected_pct);

    return (
        <div className="pnl-bar">
            <span className={`pnl-bar__fill -${severity}`} style={{width: `${width === null ? 0 : width}%`}}/>
            {paceLeft !== null && <span className="pnl-bar__pace" style={{left: `${paceLeft}%`}}/>}
        </div>
    );
}

/**
 * ¿La línea pregunta «¿El presupuesto está al día?»? (D6 de la tanda iVirtual, 09/10). Sí cuando
 * gastó `CHECK_BUDGET_RATIO` veces su presupuesto o más: un exceso así casi nunca es un desborde,
 * es un presupuesto viejo (el de RN Pad era de julio de 2024). Es el MISMO umbral que la fila del
 * Listado (`spendReason`). Una línea terminada no pregunta: su período ya cerró.
 */
export function lineNeedsBudgetCheck(line) {
    if (!line || lineIsFinished(line)) return false;
    const budget = line.budget || {};
    const allocated = Number(budget.allocated);
    const spend = Number(budget.spend);
    if (!(allocated > 0) || !Number.isFinite(spend)) return false;
    return spend / allocated >= CHECK_BUDGET_RATIO;
}

/**
 * «¿El presupuesto está al día?» con el link a la edición completa de la línea, que es donde se
 * cambia el presupuesto. Sin nada que preguntar devuelve `null`.
 *
 * Lo dibuja la card del Panel, en su propio renglón debajo de los números. La tabla de `/lines` no:
 * su celda de consumo es una barra y un número, y una frase más inflaba la fila entera.
 */
export function LineBudgetCheck({line}) {
    const {t} = useTranslation();
    if (!lineNeedsBudgetCheck(line)) return null;
    return (
        <div className="pnl-line__check-row">
            <Link className="pnl-line__check" to={`/business-unit/${line.id}/`} data-testid="pnl-line-check-budget">
                <PencilSimple size={12} weight="bold" aria-hidden="true"/>
                {t('client_panel.line_check_budget')}
            </Link>
        </div>
    );
}

/**
 * El color de la proyección. El mapeo fijo de `PROJECTION_SEVERITY_BY_STATUS` (UNDER rojo, OVER oro)
 * vale para todo, salvo cuando la LÍNEA tiene la alerta de presupuesto en rojo: ahí la proyección
 * toma el rojo de la alerta (D7). Sin esto, RN Pad mostraba «Proyecta gastar 1.491 %» en oro al lado
 * de la frase roja de su alerta y de la barra roja, y el oro se leía como «no es tan grave».
 */
function projectionTone(status, alertLevel) {
    if (alertLevel === ALERT_LEVEL_CRITIC) return alertLevelSeverity(alertLevel);
    return projectionSeverity(status);
}

/**
 * Hacia dónde cierra la línea. Cuando el backend no puede proyectar dice POR QUÉ (`reason`), y eso
 * se muestra en gris — un "0%" ahí sería mentira. `long` es la versión de la fila del Panel, donde
 * el número va suelto en una oración; en la tabla la columna ya se llama Proyección y alcanza el
 * porcentaje. Sin nada que decir devuelve `null` (el llamador pinta "—" si quiere).
 *
 * La versión larga dice QUÉ se proyecta («Proyecta gastar 63,6% del presupuesto») y lleva un ⓘ con
 * los dos montos detrás del porcentaje y de dónde sale (card 6tGAWS1a): hasta el 24/09 decía
 * «→ proyecta 63,6%» y Pablo no entendía qué significaba. Se queda en porcentaje y no en monto
 * (decisión de fede): se escanea más rápido fila contra fila. El ⓘ necesita `budget` y
 * `currency`; sin ellos no se dibuja.
 *
 * `alertLevel` (opcional): el `budget_alert_level` de la línea, para el rojo de D7
 * (`projectionTone`). Sin él, el mapeo fijo de siempre.
 */
export function LineProjection({projection, long, budget, currency, alertLevel}) {
    const {t} = useTranslation();
    const data = projection || {};

    // Línea eventual cuyo período YA cerró: no hay futuro que proyectar, el cierre es un hecho
    // consumado (el backend congela `amount` en el gasto final). El color sí sale del mismo
    // `status` de siempre: cerrar en 62% del presupuesto es igual de rojo antes y después.
    if (data.reason === 'FINISHED') {
        const finishedPct = formatDecimal(data.vs_budget_pct);
        const finishedSeverity = projectionTone(data.status, alertLevel);
        return (
            <span className={`pnl-proy ${finishedSeverity ? `-${finishedSeverity}` : ''}`}>
                {finishedPct === null
                    // Sin presupuesto no hay porcentaje posible, pero el hecho —que el período
                    // terminó— sigue valiendo: se dice sin número en vez de callarse.
                    ? t('client_panel.line_projection_finished_no_pct', 'período cerrado')
                    : t('client_panel.line_projection_finished', 'cerró en {{pct}}%', {pct: finishedPct})}
            </span>
        );
    }
    if (data.reason === 'NO_HISTORY') {
        return (
            <span className="pnl-proy -muted">
                {t('client_panel.line_projection_no_history', 'recopilando datos')}
            </span>
        );
    }
    if (data.reason === 'STALE') {
        return (
            <span className="pnl-proy -muted">
                {t('client_panel.line_projection_stale', 'sin datos recientes')}
            </span>
        );
    }

    const pctText = formatDecimal(data.vs_budget_pct);
    if (pctText === null) return null;

    const severity = projectionTone(data.status, alertLevel);
    const budgetText = formatFullMoney(currency, (budget || {}).allocated);
    const projectedText = formatFullMoney(currency, data.amount);

    // Los dos montos detrás del porcentaje y la regla con la que se calcula
    // (`SpendCloseProjectionService`: lo invertido + el ritmo diario de los últimos 7 días por los
    // días que quedan). La posición la pone `.pnl-proy__info` en `ControlLines.scss`: el cartel se
    // ancla a la fila de números, no al ⓘ, para no salirse de la pantalla cuando la fila se apila.
    const renderInfo = () => (
        <span className="info-icon-container pnl-proy__info">
            <Info size={13} weight="bold"/>
            <span className="warning-tooltip -rich">
                <span className="warning-tooltip-body">
                    <span className="warning-tooltip-text pnl-proy__tip">
                        <span className="pnl-proy__tip-row">
                            {t('client_panel.line_projection_tip_budget', 'Presupuesto: {{amount}}',
                                {amount: budgetText, interpolation: {escapeValue: false}})}
                        </span>
                        <span className="pnl-proy__tip-row">
                            {t('client_panel.line_projection_tip_projected', 'Gasto proyectado al cierre: {{amount}}',
                                {amount: projectedText, interpolation: {escapeValue: false}})}
                        </span>
                        <span className="pnl-proy__tip-row -how">
                            {t('client_panel.line_projection_tip_how',
                                'Es lo invertido hasta hoy más el ritmo diario de los últimos 7 días por los días en que la línea sigue activa hasta el cierre del período. El porcentaje es ese gasto proyectado sobre el presupuesto.')}
                        </span>
                    </span>
                </span>
            </span>
        </span>
    );

    if (!long) {
        return (
            <span className={`pnl-proy ${severity ? `-${severity}` : ''}`}>
                {t('client_panel.line_projection_short', '{{pct}}%', {pct: pctText})}
            </span>
        );
    }
    return (
        <span className={`pnl-proy ${severity ? `-${severity}` : ''}`}>
            {t('client_panel.line_projection', 'Proyecta gastar {{pct}}% del presupuesto', {pct: pctText})}
            {budgetText && projectedText && renderInfo()}
        </span>
    );
}
